'use client';

import { useState } from 'react';
import { Link } from 'react-router';
import { useLocation } from 'react-router';

import {
  Menu,
  X,
  Home,
  Trophy,
  Target,
  User,
  LogOut,
  LayoutDashboard,
  Settings,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/AuthContext';
import { ThemeToggleButton } from '@/components/ThemeToggleButton';
import { ActiveLink } from '@/components/navigation/active-link';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/** Enlaces del hub público (solo módulos listos para visitantes) */
const PUBLIC_NAV_LINKS = [
  { path: '/tournaments', label: 'Torneos', description: 'Competencias e inscripción pública', icon: Trophy },
  { path: '/activities', label: 'Actividades', description: 'Tests cognitivos de acceso libre', icon: Target },
] as const;

/** Header compartido: landing, torneos públicos y rutas del hub */
export const PublicHeader = () => {
  const location = useLocation();
  const pathname = location.pathname;

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, signOut } = useAuth();

  const loginHref = `/login?return=${encodeURIComponent(pathname)}`;
  const closeMobile = () => setIsMobileMenuOpen(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/90 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid h-[60px] grid-cols-[1fr_auto_1fr] items-center gap-4">
          <Link
            to="/"
            className="flex shrink-0 items-center gap-2.5 group justify-self-start"
            onClick={closeMobile}
            aria-label="CoreUtils — Inicio"
          >
            <img 
              src="/core-utils-logo.png" 
              alt="" 
              width={36}
              height={36}
              loading="lazy"
              className="h-9 w-9 object-contain transition-transform group-hover:scale-105" 
            />
            <span className="font-display text-xl font-bold tracking-tight text-primary">
              CoreUtils
            </span>
          </Link>

          <nav
            className="hidden md:flex items-stretch h-[60px] gap-1 justify-self-center"
            aria-label="Principal"
          >
            <ActiveLink
              href="/"
              end
              className={({ isActive }) =>
                cn('nav-tab h-full items-center gap-2', isActive && 'nav-tab-active')
              }
            >
              <Home className="h-4 w-4 shrink-0" />
              Inicio
            </ActiveLink>
            {PUBLIC_NAV_LINKS.map(({ path, label, icon: Icon }) => (
              <ActiveLink
                key={path}
                href={path}
                className={({ isActive }) =>
                  cn('nav-tab h-full items-center gap-2', isActive && 'nav-tab-active')
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                {label}
              </ActiveLink>
            ))}
          </nav>

          <div className="flex items-center gap-1.5 shrink-0 justify-self-end">
            <ThemeToggleButton className="h-9 w-9 text-muted-foreground" />
            {isAuthenticated && user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="hidden sm:flex h-9 gap-2 rounded-full pl-1 pr-3 border border-border"
                  >
                    {user.avatar_url ? (
                      <img 
                        src={user.avatar_url} 
                        alt="" 
                        width={28}
                        height={28}
                        loading="lazy"
                        className="h-7 w-7 rounded-full object-cover" 
                      />
                    ) : (
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10">
                        <User className="h-3.5 w-3.5 text-primary" />
                      </span>
                    )}
                    <span className="max-w-[120px] truncate text-sm">
                      {user.name?.split(' ')[0] || 'Cuenta'}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium truncate">{user.name || user.email}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings">
                      <Settings className="mr-2 h-4 w-4" />
                      Configuración
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => signOut()} className="text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="default"
                size="sm"
                asChild
                className="hidden sm:flex h-9 px-5"
              >
                <Link to={loginHref}>Iniciar sesión</Link>
              </Button>
            )}

            <Button
              variant="ghost"
              size="icon"
              className="md:hidden h-9 w-9"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-border py-3 animate-fade-in">
            <nav className="flex flex-col gap-1" aria-label="Principal móvil">
              <ActiveLink
                href="/"
                end
                onClick={closeMobile}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm font-medium',
                    isActive ? 'nav-item-active' : 'text-muted-foreground hover:text-foreground',
                  )
                }
              >
                <Home className="h-4 w-4" />
                Inicio
              </ActiveLink>
              {PUBLIC_NAV_LINKS.map(({ path, label, description, icon: Icon }) => (
                <ActiveLink
                  key={path}
                  href={path}
                  onClick={closeMobile}
                  className={({ isActive }) =>
                    cn(
                      'flex items-start gap-3 rounded-lg px-3.5 py-2.5 text-sm',
                      isActive ? 'nav-item-active' : 'hover:text-foreground',
                    )
                  }
                >
                  <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="flex flex-col items-start">
                    <span className="font-medium">{label}</span>
                    <span className="text-xs text-muted-foreground">{description}</span>
                  </span>
                </ActiveLink>
              ))}
              {!isAuthenticated && (
                <Button className="mt-2 w-full" asChild>
                  <Link to={loginHref} onClick={closeMobile}>
                    Iniciar sesión
                  </Link>
                </Button>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
